/**
 * OTPEntryStep Component
 * Second step of the onboarding flow - OTP verification.
 */

import React, { useState } from "react";
import {
  Box,
  OTPInput,
  Button,
  Typography,
  Link,
  IconButton,
  ArrowBackIcon,
} from "../../design-system";
import { useCsrfToken } from "../../../hooks";
import { OTP_STEP } from "../constants/onboardingText";
import { colors } from "../../design-system/colors";

const OTP_LENGTH = 6;

interface OTPEntryStepProps {
  email: string;
  onSubmit: (code: string, csrfToken: string) => Promise<void>;
  onResend: (email: string, csrfToken: string) => Promise<void>;
  onBack: () => void;
  loading?: boolean;
  error?: string | null;
  resendCooldown?: number;
}

const OTPEntryStep: React.FC<OTPEntryStepProps> = ({
  email,
  onSubmit,
  onResend,
  onBack,
  loading = false,
  error = null,
  resendCooldown = 0,
}) => {
  const [otpValue, setOtpValue] = useState<string[]>(
    Array(OTP_LENGTH).fill(""),
  );
  const csrfToken = useCsrfToken();

  const handleOTPChange = (value: string[]) => {
    setOtpValue(value);
  };

  const handleOTPComplete = async (code: string) => {
    if (csrfToken) {
      await onSubmit(code, csrfToken);
    }
  };

  const handleResend = async () => {
    if (csrfToken && resendCooldown === 0) {
      await onResend(email, csrfToken);
    }
  };

  const isOTPComplete =
    otpValue.every((digit) => digit !== "") && otpValue.length === OTP_LENGTH;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        width: "100%",
        gap: "24px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          width: "100%",
        }}
      >
        <IconButton
          onClick={onBack}
          ariaLabel={OTP_STEP.BACK_BUTTON_ARIA}
          size="medium"
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>

      <Typography variant="headingDisplay" color="white" component="h1">
        {OTP_STEP.TITLE}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <Typography variant="subheading" color="secondary">
          {OTP_STEP.DESCRIPTION_PREFIX} {email}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          width: "100%",
        }}
      >
        <Typography variant="subheading" color="white">
          {OTP_STEP.CODE_LABEL}
        </Typography>
        <OTPInput
          value={otpValue}
          onChange={handleOTPChange}
          error={!!error}
          onComplete={handleOTPComplete}
        />
        {error && (
          <Typography variant="body" color="error" sx={{ marginTop: "8px" }}>
            {error}
          </Typography>
        )}
      </Box>

      <Button
        variant="primary"
        loading={loading}
        disabled={!isOTPComplete || loading}
        fullWidth
        onClick={async () => {
          if (isOTPComplete && csrfToken) {
            await onSubmit(otpValue.join(""), csrfToken);
          }
        }}
      >
        {OTP_STEP.SUBMIT_BUTTON}
      </Button>

      <Box
        sx={{
          display: "flex",
          width: "100%",
        }}
      >
        {resendCooldown > 0 ? (
          <Box sx={{ display: "flex", gap: "4px" }}>
            <Typography variant="subheading" color="secondary">
              {OTP_STEP.RESEND_PREFIX}
            </Typography>
            <Typography
              variant="subheading"
              sx={{
                color: colors.text.disabled,
                fontWeight: 400,
              }}
            >
              {OTP_STEP.RESEND_COOLDOWN(resendCooldown)}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", gap: "4px" }}>
            <Typography variant="subheading" color="secondary">
              {OTP_STEP.RESEND_PREFIX}
            </Typography>
            <Link
              variant="action"
              component="button"
              type="button"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                handleResend();
              }}
            >
              {OTP_STEP.RESEND_LINK}
            </Link>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default OTPEntryStep;
